import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/verticals", label: "Verticals", id: "verticals" },
  { to: "/csr", label: "CSR", id: "csr" },
  { to: "/newsroom", label: "Newsroom", id: "news" },
  { to: "/blog", label: "Blog", id: "blog" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/careers", label: "Careers", id: "career" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 glass-nav transition-shadow ${scrolled ? "shadow-2xl" : ""}`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
          <img src="/logo-mark.png" alt="SRAM & MRAM" className="h-9 w-auto transition-transform duration-500 group-hover:scale-105" />
          <span className="font-display font-extrabold text-lg tracking-tight text-white leading-none">
            SRAM<span className="text-emerald-400"> &amp; </span>MRAM
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.id}-link`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-emerald-400" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/admin"
            data-testid="nav-admin-btn"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-emerald-400 border border-slate-700 hover:border-emerald-500/50 rounded-full px-3 py-1.5 transition-colors"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Admin
          </Link>
          <button
            className="lg:hidden text-slate-200 p-2"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-slate-800 bg-slate-950/95 backdrop-blur-xl px-4 py-4 space-y-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-mobile-${l.id}-link`}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-md text-base font-medium ${
                  isActive ? "text-emerald-400 bg-emerald-500/10" : "text-slate-200"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/admin" data-testid="nav-mobile-admin-link" className="block px-3 py-2.5 text-slate-400">
            Admin Login
          </Link>
        </div>
      )}
    </header>
  );
}
